import { AUTH_API_PATHS } from "@/constants/apiPaths";
import { cookieProvider } from "@/providers/CookieProvider";

interface TokenResponse {
    authToken: string;
    refreshToken: string;
}

class AuthTokenService {
    private static AUTH_TOKEN_KEY = 'authToken';
    private static REFRESH_TOKEN_KEY = 'refreshToken';

    static getAuthToken(): string | null {
        return cookieProvider.getAuthToken();;
    }

    private static isTokenExpired(token: string): boolean {
        try {
            const [, payload] = token.split('.');
            const decodedPayload = JSON.parse(atob(payload));
            return decodedPayload.exp * 1000 < Date.now();
        } catch {
            return true;
        }
    }

    static getRefreshToken(): string | null {
        return cookieProvider.getRefreshToken();
    }


    static setTokens(tokens: TokenResponse) {
        cookieProvider.set(this.AUTH_TOKEN_KEY, tokens.authToken);
        cookieProvider.set(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
    }

    static clearTokens() {
        cookieProvider.clearAuthData();
    }


    static async ensureValidToken(): Promise<string> {
        const authToken = this.getAuthToken();
        if (!authToken) {
            throw new Error('No auth token found');
        }

        try {
            // Check if token is expired (you might want to decode JWT and check exp)
            const isExpired = this.isTokenExpired(authToken);
            if (isExpired) {
                const refreshToken = this.getRefreshToken();
                if (!refreshToken) {
                    throw new Error('No refresh token found');
                }

                const response = await fetch(AUTH_API_PATHS.REFRESH_TOKEN, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ refreshToken }),
                });

                if (!response.ok) {
                    throw new Error('Failed to refresh token');
                }

                const tokens: TokenResponse = await response.json();
                this.setTokens(tokens);
                return tokens.authToken;
            }

            return authToken;
        } catch (error) {
            this.clearTokens();
            throw error;
        }
    }
}

export default AuthTokenService;
